import { hasBankWithdrawals } from '../../util/BankWithdrawals'
import { observer } from 'mobx-react'
import React, { ReactElement, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'

import { PrimaryButton } from '../../components/Button'
import Checkbox from '../../components/Checkbox'
import Dropdown, { DropdownItem } from '../../components/Dropdown'
import { InputLabel, TextInputWithLabel } from '../../components/Input'
import Modal, { ModalFooter, ModalProps } from '../../components/Modal'
import Radom, { Address, FiatAccountDetails } from '../../state/Radom'
import User from '../../state/User'
import FiatRamps from '../../state/FiatRamps'
import { ManagedPaymentMethod, getPaymentMethods } from '../../util/Managed'
import { generateRoundFlagIconFromCountryCode } from '../../util/Flags'
import { errorToast, successToast, useTestMode } from '../../util/Util'
import { AddressForm } from '../Verify/AddressForm'
import { Countries } from '../Verify/Countries'

type AccountModalProps = ModalProps & {
  onAccountCreated: () => void
  accountType?: AccountType
}

export enum AccountType {
  Crypto,
  Fiat
}

enum FiatAccountType {
  US = 'US',
  IBAN = 'EU (IBAN)',
  SWIFT = 'SWIFT',
}

enum AccountOwnerType {
  Individual = 'Individual',
  Business = 'Business',
}

const AccountTypeGrid = styled.div`
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(2, minmax(0, 1fr));

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`

const AccountTypeTile = styled.button<{ $active?: boolean }>`
  align-items: center;
  background: ${({ $active }) => $active ? 'var(--surface-hover, #f7f7f8)' : 'var(--surface, #ffffff)'};
  border: 1px solid ${({ $active }) => $active ? 'var(--border-strong, #d7dbe3)' : 'var(--border, #e3e7ef)'};
  border-radius: 14px;
  color: var(--ink, #20242d);
  cursor: pointer;
  display: grid;
  gap: 10px;
  grid-template-columns: auto minmax(0, 1fr) auto;
  min-height: 52px;
  padding: 9px 12px;
  text-align: left;
  transition: 0.16s ease background-color, 0.16s ease border-color;
  width: 100%;

  &:hover {
    background: ${({ $active }) => $active ? 'var(--surface-hover, #f7f7f8)' : 'var(--surface-muted, #f8fafc)'};
    border-color: ${({ $active }) => $active ? 'var(--border-strong, #d7dbe3)' : 'var(--border-strong, #d8deea)'};
  }
`

const AccountTypeTileTitle = styled.div`
  color: var(--ink, #20242d);
  font-size: 12.5px;
  font-weight: 500;
  line-height: 1.25;
`

const AccountTypeTileSubtitle = styled.div`
  color: var(--text-muted, #6b7280);
  font-size: 11.5px;
  line-height: 1.35;
`

const AccountTypeTileIcon = styled.div`
  align-items: center;
  display: flex;
  height: 24px;
  justify-content: flex-start;
  min-width: 48px;
`

const FlagStack = styled.div`
  align-items: center;
  display: flex;
  min-width: 0;

  > * + * {
    margin-left: -7px;
  }
`

const IconBadge = styled.div`
  align-items: center;
  background: var(--surface, #ffffff);
  border: 1px solid var(--border, #e4e8ef);
  border-radius: 999px;
  box-shadow: 0 4px 10px rgba(15, 23, 42, 0.05);
  display: flex;
  flex: 0 0 auto;
  height: 24px;
  justify-content: center;
  overflow: hidden;
  width: 24px;
`

const TileMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
`

const RailMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`

const RailLabel = styled.span`
  color: var(--ink, #20242d);
  font-size: 14px;
  font-weight: 500;
  line-height: 1.3;
`

const RailHint = styled.span`
  color: var(--icon-muted, #747894);
  font-size: 12px;
  line-height: 1.35;
`

const InlineNotice = styled.div`
  border: 1px solid #fde1c5;
  background: #fff8ef;
  border-radius: 12px;
  color: #8b5a20;
  font-size: 12px;
  line-height: 1.45;
  padding: 11px 12px;
`

const ModalIntro = styled.div`
  padding: 13px 14px;
  border-radius: 16px;
  border: 1px solid var(--border-soft, #eceff3);
  background: var(--surface-subtle, #f9fafb);
  color: var(--text-muted, #5f6675);
  font-size: 13.5px;
  line-height: 1.55;
`

const ModalSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
  border-radius: 18px;
  border: 1px solid var(--border-soft, #eceff3);
  background: var(--surface-subtle, #fcfcfd);
`

const SectionHeading = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`

const SectionTitle = styled.div`
  color: var(--ink, #20242d);
  font-size: 15px;
  font-weight: 500;
`

const SectionDescription = styled.div`
  color: var(--text-muted, #6b7280);
  font-size: 13px;
  line-height: 1.5;
`

const FormStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const ResponsiveFields = styled.div`
  display: grid;
  gap: 14px;
  grid-template-columns: repeat(2, minmax(0, 1fr));

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
`

const CreateAccountModal = observer(({ ...props }: AccountModalProps): ReactElement => {
  const [accountName, setAccountName] = useState('')
  const [accountType, setAccountType] = useState<AccountType>(props.accountType ?? AccountType.Crypto)

  // Crypto options
  const [selectedCryptoType, setSelectedCryptoType] = useState<ManagedPaymentMethod>()
  const [cryptoAddress, setCryptoAddress] = useState<string>()

  // Fiat options
  const [accountOwnerType, setAccountOwnerType] = useState<AccountOwnerType>()
  const [accountOwnerName, setAccountOwnerName] = useState<string>()
  const [fiatAccountType, setFiatAccountType] = useState<FiatAccountType>()
  const [accountOwnerAddress, setAccountOwnerAddress] = useState<Address>({} as any)
  const [iban, setIban] = useState<string>()
  const [bic, setBic] = useState<string>()
  const [bankName, setBankName] = useState('')
  const [accountNumber, setAccountNumber] = useState('')
  const [routingNumber, setRoutingNumber] = useState('')
  const [bankAccountType, setBankAccountType] = useState<'CHECKING' | 'SAVINGS'>('CHECKING')
  const [bankAddress, setBankAddress] = useState<Address>({ country: 'USA', streetLine1: '', city: '' })
  const [email, setEmail] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const bankAvailability = User.organization?.bankWithdrawals
  const bankAccountsEnabled = FiatRamps.cashWithdrawalEnabled || hasBankWithdrawals(bankAvailability)
  const usesBastion = fiatAccountType === FiatAccountType.US
    ? !!bankAvailability?.usdAssets.length
    : !!bankAvailability?.eurAssets.length
  const selectedRailUnavailable = fiatAccountType === FiatAccountType.US
    ? !bankAvailability?.usdAssets.length
    : !bankAvailability?.eurAssets.length && !FiatRamps.cashWithdrawalEnabled
  const ownerName = usesBastion && accountOwnerType === AccountOwnerType.Individual
    ? `${firstName.trim()} ${lastName.trim()}`.trim()
    : accountOwnerName
  const completeAddress = (address: Address): boolean => !!(
    address.streetLine1?.trim() && address.city?.trim() && address.country &&
    address.postalCode?.trim() && address.subdivision?.trim()
  )
  const bastionDetailsMissing = usesBastion && (!bankName.trim() || !email.includes('@') ||
    !completeAddress(accountOwnerAddress) ||
    (accountOwnerType === AccountOwnerType.Individual && (!firstName.trim() || !lastName.trim())) ||
    (fiatAccountType === FiatAccountType.US && (!/^\d{4,17}$/.test(accountNumber) ||
      !/^\d{9}$/.test(routingNumber) || !completeAddress(bankAddress))))

  const [isLoading, setIsLoading] = useState(false)
  const [testMode] = useTestMode()
  const [shouldHideMonero, setShouldHideMonero] = useState(true)

  useEffect(() => {
    User.getOrganization()
      .then((o) => setShouldHideMonero(!o.features?.isMoneroEnabled))
      .catch(() => setShouldHideMonero(true))
  }, [])

  useEffect(() => {
    if (selectedCryptoType && !isVisibleCryptoMethod(selectedCryptoType)) {
      setSelectedCryptoType(undefined)
    }
  }, [selectedCryptoType, shouldHideMonero])

  const isVisibleCryptoMethod = (method: ManagedPaymentMethod): boolean => {
    if (method.name === 'Monero' || method.name === 'MoneroStagenet') {
      return !shouldHideMonero
    }

    return true
  }

  const cryptoMethodIcon = getPaymentMethods()
    .find(t => t.ticker === 'BTC' && !t.hostChain && !t.isFiat && (testMode ? t?.isTestnet : !t?.isTestnet))

  const cryptoTileIcons = useMemo(() => {
    const currentModeMethods = getPaymentMethods()
      .filter((method) => !method.isHidden)
      .filter((method) => testMode ? !!method.isTestnet : !method.isTestnet)

    return [
      currentModeMethods.find((method) => method.ticker === 'BTC' && !method.hostChain),
      currentModeMethods.find((method) => method.ticker === 'ETH'),
      currentModeMethods.find((method) => method.ticker === 'USDC')
    ].filter(Boolean) as ManagedPaymentMethod[]
  }, [testMode])

  const fiatAccountTypeOptions: Array<{
    value: FiatAccountType
    label: string
    hint: string
    flags: string[]
    comingSoon?: boolean
  }> = [
    {
      value: FiatAccountType.IBAN,
      label: 'EUR account (SEPA)',
      hint: 'Euro payouts to an IBAN bank account',
      flags: ['EU', 'GB']
    },
    {
      value: FiatAccountType.US,
      label: 'USD account (wire)',
      hint: bankAvailability?.usdAssets.length ? 'USD payouts over Fedwire' : 'Not enabled for this organization',
      flags: ['US']
    }
  ]

  const availableCryptoMethods = useMemo(() => getPaymentMethods()
    .filter((method) => !method.hostChain)
    .filter((method) => method.name !== selectedCryptoType?.name)
    .filter((method) => !method.isDisabled)
    .filter((method) => !method.isHidden)
    .filter((method) => testMode ? !!method.isTestnet : !method.isTestnet)
    .filter((method) => !method.isFiat)
    .filter(isVisibleCryptoMethod), [selectedCryptoType?.name, testMode, shouldHideMonero])

  const renderFlagBadge = (countryCode: string): ReactElement => (
    <IconBadge key={countryCode}>
      {generateRoundFlagIconFromCountryCode(countryCode, 18)}
    </IconBadge>
  )

  const renderMethodBadge = (method: ManagedPaymentMethod): ReactElement => (
    <IconBadge key={`${method.name}-${method.ticker}`}>
      <img
        src={method.logo}
        style={{
          height: 16,
          width: 'auto',
          maxWidth: 16
        }} />
    </IconBadge>
  )

  const renderRailContent = (
    label: string,
    hint: string,
    flags: string[]
  ): ReactElement => (
    <div style={{
      alignItems: 'center',
      display: 'flex',
      gap: 10,
      minHeight: 34
    }}>
      <FlagStack>
        {flags.map(renderFlagBadge)}
      </FlagStack>
      <RailMeta>
        <RailLabel>{label}</RailLabel>
        <RailHint>{hint}</RailHint>
      </RailMeta>
    </div>
  )

  const createCryptoAccount = async (): Promise<void> => {
    if (!selectedCryptoType || !cryptoAddress) {
      return
    }

    setIsLoading(true)
    try {
      await Radom.createWithdrawalAccount({
        name: accountName,
        accountDetails: {
          Crypto: {
            network: selectedCryptoType?.name,
            address: cryptoAddress
          }
        }
      })
      props.onAccountCreated()
      successToast('Withdrawal account created')
    } catch (err) {
      errorToast(err.response?.data?.error || err.message)
    }
    setIsLoading(false)
  }

  const createFiatAccount = async (): Promise<void> => {
    if (selectedRailUnavailable || bastionDetailsMissing) {
      return
    }

    if (
      !accountName ||
      !fiatAccountType ||
      !accountOwnerType ||
      !ownerName ||
      !accountOwnerAddress.country ||
      !accountOwnerAddress.city ||
      !accountOwnerAddress.streetLine1
    ) {
      errorToast('Fill in the remaining account details before creating the account')
      return
    }

    if (fiatAccountType === FiatAccountType.IBAN && (!iban || !bic)) {
      errorToast('Enter the IBAN and BIC for this account')
      return
    }

    const accountType: FiatAccountDetails = fiatAccountType === FiatAccountType.US
      ? {
        us: {
          accountNumber,
          routingNumber,
          bankName: bankName.trim(),
          bankAccountType,
          bankAddress,
          addressV2: accountOwnerAddress
        }
      }
      : {
        iban: {
          iban: (iban ?? '').replace(/\s+/g, '').toUpperCase(),
          bic: (bic ?? '').trim().toUpperCase(),
          bankName: usesBastion ? bankName.trim() : undefined,
          addressV2: usesBastion ? accountOwnerAddress : { ...accountOwnerAddress, postalCode: accountOwnerAddress.postalCode || 'N/A' }
        }
      }

    setIsLoading(true)
    try {
      await Radom.createWithdrawalAccount({
        name: accountName,
        accountDetails: {
          Fiat: {
            accountOwnerName: ownerName,
            firstName: usesBastion ? firstName.trim() || undefined : undefined,
            lastName: usesBastion ? lastName.trim() || undefined : undefined,
            email: usesBastion ? email.trim() : undefined,
            accountOwnerType,
            accountType
          }
        }
      })
      props.onAccountCreated()
      successToast('Withdrawal account created')
    } catch (err) {
      errorToast(err.response?.data?.error || err.message)
    }
    setIsLoading(false)
  }

  const createAccount = async (): Promise<void> => {
    if (accountType === AccountType.Crypto) {
      return await createCryptoAccount()
    } else {
      if (!bankAccountsEnabled) return
      return await createFiatAccount()
    }
  }

  const renderCryptoForm = (): ReactElement => (
    <ModalSection>
      <SectionHeading>
        <SectionTitle>Crypto destination</SectionTitle>
        <SectionDescription>Save a wallet address and network for future payouts.</SectionDescription>
      </SectionHeading>

      <FormStack>
        <TextInputWithLabel
          label='Account nickname'
          placeholder='Enter a name for this address'
          disabled={isLoading}
          required
          value={accountName}
          onChange={e => {
            const el = e.target as HTMLInputElement
            setAccountName(el.value)
          }}
          onBlur={() => {
            setAccountName(prev => prev.trim())
          }}
        />
        <FieldGroup>
          <InputLabel>Network</InputLabel>
          <Dropdown
            disabled={isLoading}
            outerStyle={{ minHeight: 46 }}
            selectedContent={
              selectedCryptoType
                ? <div style={{
                  fontSize: 14,
                  alignItems: 'center',
                  display: 'flex',
                  gap: 10,
                  minHeight: 32
                }}>
                  <img
                    src={selectedCryptoType.logo}
                    style={{
                      height: 20,
                      width: 'auto',
                      maxWidth: 20
                    }} />
                  <span style={{ color: 'var(--ink, #20242d)', fontWeight: 500 }}>
                    {selectedCryptoType.ticker} · {selectedCryptoType.name}
                  </span>
                </div>
                : <span style={{ fontSize: 14, color: 'var(--icon-muted, #747894)' }}>Select network</span>
            }
            dropdownContent={
              availableCryptoMethods
                .map(t =>
                  <DropdownItem
                    key={t.name}
                    onClick={() => {
                      setSelectedCryptoType(t)
                    }}
                    style={{
                      fontSize: 14,
                      alignItems: 'center',
                      display: 'flex',
                      gap: 10
                    }}>
                    <img
                      src={t.logo}
                      style={{
                        height: 20,
                        width: 'auto',
                        maxWidth: 20
                      }} />
                    <span style={{ color: 'var(--ink, #20242d)', fontWeight: 500 }}>{t.ticker} · {t.name}</span>
                  </DropdownItem>
                )
            } />
        </FieldGroup>

        <TextInputWithLabel
          label={'Address'}
          placeholder={'Enter address'}
          disabled={isLoading}
          required
          value={cryptoAddress}
          onChange={e => {
            const el = (e.target as HTMLInputElement)
            const value = el.value
            setCryptoAddress(value)
          }}
          onBlur={() => {
            setCryptoAddress(prev => {
              if (prev) return prev.trim()
              return prev
            })
          }}
        />
      </FormStack>
    </ModalSection>
  )

  const renderBankForm = (): ReactElement => (
    <FormStack>
      <ModalSection>
        <SectionHeading>
          <SectionTitle>Account profile</SectionTitle>
          <SectionDescription>Name this withdrawal destination and choose who owns it.</SectionDescription>
        </SectionHeading>

        <TextInputWithLabel
          label='Account nickname'
          placeholder='Enter a nickname for this account'
          disabled={isLoading}
          required
          value={accountName}
          onChange={e => {
            const el = (e.target as HTMLInputElement)
            const value = el.value
            setAccountName(value)
          }}
          onBlur={() => {
            setAccountName(prev => prev.trim())
          }}
        />

        <ResponsiveFields>
          <FieldGroup>
            <InputLabel>Account owner type</InputLabel>
            <Dropdown
              key={accountOwnerType}
              disabled={isLoading}
              outerStyle={{ minHeight: 46 }}
              selectedContent={
                accountOwnerType
                  ? <div style={{
                    fontSize: 14,
                    alignItems: 'center',
                    display: 'flex',
                    gap: 10,
                    color: 'var(--ink, #20242d)'
                  }}>
                    {accountOwnerType}
                  </div>
                  : <span style={{ fontSize: 14, color: 'var(--icon-muted, #747894)' }}>Account owner type</span>
              }
              dropdownContent={
                [AccountOwnerType.Individual, AccountOwnerType.Business].map(c =>
                  <DropdownItem
                    key={c}
                    onClick={() => {
                      setAccountOwnerType(c)
                    }}
                    style={{
                      fontSize: 14,
                      alignItems: 'center',
                      display: 'flex',
                      gap: 10
                    }}>

                    {c}
                  </DropdownItem>
                )
              } />
          </FieldGroup>

          {
            accountOwnerType && !(usesBastion && accountOwnerType === AccountOwnerType.Individual) &&
            <TextInputWithLabel
              style={{ flexGrow: 1 }}
              label={accountOwnerType === AccountOwnerType.Business ? 'Business full name' : 'Account holder full name'}
              placeholder={accountOwnerType === AccountOwnerType.Business
                ? 'Enter business\'s name'
                : 'Enter the account holder\'s name'}
              disabled={isLoading}
              required
              value={accountOwnerName}
              onChange={e => {
                const el = (e.target as HTMLInputElement)
                const value = el.value
                setAccountOwnerName(value)
              }}
              onBlur={() => {
                setAccountOwnerName(prev => {
                  if (prev) return prev.trim()
                  return prev
                })
              }}
            />
          }
          {
            accountOwnerType &&
            <span style={{ color: 'var(--text-faint, #8a919e)', fontSize: 12, lineHeight: 1.45, marginTop: -4 }}>
              Must exactly match the name registered on the bank account. Banks verify the
              payee name before accepting transfers.
            </span>
          }
          {usesBastion && accountOwnerType === AccountOwnerType.Individual && <>
            <TextInputWithLabel label='First name' aria-label='First name' value={firstName} required disabled={isLoading}
              onChange={e => setFirstName((e.target as HTMLInputElement).value)} />
            <TextInputWithLabel label='Last name' aria-label='Last name' value={lastName} required disabled={isLoading}
              onChange={e => setLastName((e.target as HTMLInputElement).value)} />
          </>}
          {usesBastion && <TextInputWithLabel label='Account holder email' aria-label='Account holder email' value={email} required disabled={isLoading}
            onChange={e => setEmail((e.target as HTMLInputElement).value)} />}
        </ResponsiveFields>

      </ModalSection>

      <ModalSection>
        <SectionHeading>
          <SectionTitle>Settlement details</SectionTitle>
          <SectionDescription>
            Select the bank rail and provide the account details used for payouts.
          </SectionDescription>
        </SectionHeading>

        <FieldGroup>
          <InputLabel>Bank account type</InputLabel>
          <Dropdown
            key={fiatAccountType}
            disabled={isLoading}
            outerStyle={{ minHeight: 46 }}
            selectedContent={
              fiatAccountType
                ? (() => {
                  const selectedRail = fiatAccountTypeOptions.find((option) => option.value === fiatAccountType)
                  return selectedRail
                    ? renderRailContent(selectedRail.label, selectedRail.hint, selectedRail.flags)
                    : <span style={{ fontSize: 14, color: 'var(--ink, #20242d)' }}>{fiatAccountType}</span>
                })()
                : <span style={{ fontSize: 14, color: 'var(--icon-muted, #747894)' }}>Select bank account type</span>
            }
            dropdownContent={
              fiatAccountTypeOptions.map((option) =>
                <DropdownItem
                  key={option.value}
                  onClick={() => {
                    setFiatAccountType(option.value)
                  }}
                  style={{
                    fontSize: 14,
                    alignItems: 'center',
                    display: 'flex',
                    gap: 10,
                    padding: '11px 12px'
                  }}>
                  {renderRailContent(option.label, option.hint, option.flags)}
                </DropdownItem>
              )
            } />
        </FieldGroup>

        {
          fiatAccountType === FiatAccountType.IBAN &&
          <ResponsiveFields>
            <TextInputWithLabel
              label={'IBAN'}
              placeholder={'Enter IBAN'}
              disabled={isLoading}
              required
              value={iban}
              onChange={e => {
                const el = (e.target as HTMLInputElement)
                const value = el.value
                setIban(value)
              }}
              onBlur={() => {
                setIban(prev => {
                  if (!prev) return prev
                  return prev.replace(/\s+/g, '').toUpperCase()
                })
              }}
            />
            <TextInputWithLabel
              label="BIC"
              placeholder="Enter BIC"
              disabled={isLoading}
              required
              value={bic}
              onChange={e => {
                const el = e.target as HTMLInputElement
                setBic(el.value)
              }}
              onBlur={() => {
                setBic(prev => {
                  if (!prev) return prev
                  return prev.replace(/\s+/g, '').toUpperCase()
                })
              }}
            />
          </ResponsiveFields>
        }

        {usesBastion && <TextInputWithLabel label='Bank name' aria-label='Bank name' value={bankName} required disabled={isLoading}
          onChange={e => setBankName((e.target as HTMLInputElement).value)} />}
        {fiatAccountType === FiatAccountType.US && usesBastion && <>
          <ResponsiveFields>
            <TextInputWithLabel label='Account number' aria-label='Account number' value={accountNumber} required disabled={isLoading}
              onChange={e => setAccountNumber((e.target as HTMLInputElement).value.trim())} />
            <TextInputWithLabel label='Wire routing number' aria-label='Wire routing number' value={routingNumber} required disabled={isLoading}
              onChange={e => setRoutingNumber((e.target as HTMLInputElement).value.trim())} />
          </ResponsiveFields>
          <FieldGroup>
            <InputLabel>Account type</InputLabel>
            <select aria-label='Checking or savings' value={bankAccountType} disabled={isLoading}
              onChange={e => setBankAccountType(e.target.value as 'CHECKING' | 'SAVINGS')}>
              <option value='CHECKING'>Checking</option><option value='SAVINGS'>Savings</option>
            </select>
          </FieldGroup>
          <SectionTitle>Bank address</SectionTitle>
          <SectionDescription>Use the bank address from its wire instructions.</SectionDescription>
          <AddressForm state={bankAddress} onUpdate={setBankAddress} requireRegion />
        </>}
        {fiatAccountType && selectedRailUnavailable && <InlineNotice>
          This bank rail is not enabled for your organization. Contact support to enable it.
        </InlineNotice>}

      </ModalSection>

      {
        !selectedRailUnavailable &&
      <ModalSection>
        <SectionHeading>
          <SectionTitle>Account holder address</SectionTitle>
          <SectionDescription>Use the legal address associated with the account owner or business.</SectionDescription>
        </SectionHeading>
        <AddressForm state={accountOwnerAddress} onUpdate={setAccountOwnerAddress} requireRegion={usesBastion} />
      </ModalSection>
      }
    </FormStack>
  )

  const isFiatVerificationBlocked = accountType === AccountType.Fiat && !hasBankWithdrawals(bankAvailability) && User.organization?.verificationStatus !== 'Verified'

  return <Modal {...props} innerStyles={props.presentation === 'drawer' ? undefined : { width: 680 }}>
    <div style={{
      display: 'flex',
      flexGrow: 1,
      flexDirection: 'column',
      justifyContent: 'start',
      gap: 16
    }}>
      <ModalIntro>
        Save a withdrawal destination once, then reuse it across payouts without re-entering the same details.
      </ModalIntro>
      <div style={{ display: 'flex', flexGrow: 1, flexDirection: 'column', rowGap: 16 }}>
        {props.accountType === undefined && <ModalSection>
          <SectionHeading>
            <SectionTitle>Account type</SectionTitle>
            <SectionDescription>
              Choose whether this destination is a crypto wallet or a bank account.
            </SectionDescription>
          </SectionHeading>
          <AccountTypeGrid>
            <AccountTypeTile
              disabled={isLoading}
              onClick={() => setAccountType(AccountType.Crypto)}
              $active={accountType === AccountType.Crypto}
              type='button'>
              <AccountTypeTileIcon>
                {cryptoMethodIcon
                  ? <FlagStack>
                    {cryptoTileIcons.slice(0, 3).map(renderMethodBadge)}
                  </FlagStack>
                  : <span style={{ fontSize: 16 }}>+</span>}
              </AccountTypeTileIcon>
              <TileMeta>
                <AccountTypeTileTitle>Crypto address</AccountTypeTileTitle>
                <AccountTypeTileSubtitle>Wallet network and address</AccountTypeTileSubtitle>
              </TileMeta>
              <Checkbox isRadio checked={accountType === AccountType.Crypto} color='#111111' />
            </AccountTypeTile>

            <AccountTypeTile
              disabled={isLoading || !bankAccountsEnabled}
              onClick={() => setAccountType(AccountType.Fiat)}
              $active={accountType === AccountType.Fiat}
              type='button'>
              <AccountTypeTileIcon>
                <FlagStack>
                  {renderFlagBadge('US')}
                  {renderFlagBadge('EU')}
                  {renderFlagBadge('GB')}
                </FlagStack>
              </AccountTypeTileIcon>
              <TileMeta>
                <AccountTypeTileTitle>Bank account</AccountTypeTileTitle>
                <AccountTypeTileSubtitle>
                  {bankAccountsEnabled ? 'Bank withdrawals via wire or SEPA' : 'Temporarily unavailable'}
                </AccountTypeTileSubtitle>
              </TileMeta>
              <Checkbox isRadio checked={accountType === AccountType.Fiat} color='#111111' />
            </AccountTypeTile>
          </AccountTypeGrid>
        </ModalSection>
        }
        {accountType === AccountType.Crypto && renderCryptoForm()}

        {
          accountType === AccountType.Fiat && !bankAccountsEnabled &&
          <InlineNotice>
            Bank withdrawal accounts and fiat off-ramping are temporarily unavailable.
          </InlineNotice>
        }

        {
          bankAccountsEnabled && isFiatVerificationBlocked &&
          <InlineNotice>
            Verify the organization to add bank accounts in live mode.
          </InlineNotice>
        }

        {
          bankAccountsEnabled && accountType === AccountType.Fiat &&
          !isFiatVerificationBlocked &&
          renderBankForm()
        }
      </div>
    </div>

    {
      accountType === AccountType.Crypto && <ModalFooter $fullWidthAction>
        <PrimaryButton
          disabled={!accountName || !selectedCryptoType || !cryptoAddress}
          isLoading={isLoading}
          style={{ width: '100%' }}
          onClick={() => {
            createAccount()
          }}>
          Create withdrawal account
        </PrimaryButton>
      </ModalFooter>
    }

    {
      bankAccountsEnabled && accountType === AccountType.Fiat &&
      !isFiatVerificationBlocked &&
      <ModalFooter $fullWidthAction>
        <PrimaryButton
          disabled={
            selectedRailUnavailable ||
            bastionDetailsMissing ||
            !accountName ||
            !fiatAccountType ||
            !accountOwnerType ||
            !ownerName ||
            !accountOwnerAddress.country ||
            !accountOwnerAddress.city ||
            !accountOwnerAddress.streetLine1 ||
            (accountOwnerAddress.country === 'USA' && !accountOwnerAddress.subdivision) ||
            (
              !!Countries.find(s => s.alpha3 === accountOwnerAddress.country)?.postal_code_format &&
              !accountOwnerAddress.postalCode
            ) ||
            (
              fiatAccountType === FiatAccountType.IBAN &&
              (!iban || !bic)
            )
          }
          isLoading={isLoading}
          style={{ width: '100%' }}
          onClick={() => {
            createAccount()
          }}>
          Create withdrawal account
        </PrimaryButton>
      </ModalFooter>
    }
  </Modal>
})

export default CreateAccountModal
